import type {
  UnityMessage,
  UnityMessageType,
  UnityMessageData,
} from "@/betby/api/types";
import LogUtil from "@/betby/utils/LogUtil";
import {
  getHostBridge,
  registerHostBridge,
  type HostBridge,
} from "@/betby/adapters/host/hostBridge";
import { windowHostBridge } from "@/betby/adapters/host/windowHostBridge";
import { executeBetbyKill } from "@/betby/infrastructure/betby/betbyRuntime";
import { getUnityMessageRuntime } from "@/betby/infrastructure/host/unityMessageRuntime";
import {
  getGlobalUiRuntime,
  type NetworkRetryModalCallbacks,
} from "@/betby/infrastructure/ui/globalUiRuntime";
import { handleUnityReceiveMessage } from "@/betby/models/host/unityReceiveMessageService";

export const UNITY_MESSAGE_TYPES = {
  BACK_CLICK: "LoggedOut" as const,
  RECHARGE: "TurnShop" as const,
  LOGIN: "SDKLogin" as const,
  TOKEN_EXPIRED: "LoggedOut" as const,
  CURRENCY_CHANGE: "MoneyType" as const,
  GET_WALLET: "GetWallet" as const,
  SDK_READY: "SDKReady" as const,
} as const;

function isPostMessageSupported(): boolean {
  return (
    getHostBridge() !== null ||
    (typeof window !== "undefined" && typeof window.postMessage === "function")
  );
}

function getActiveHostBridge(): HostBridge {
  const bridge = getHostBridge();
  if (bridge) {
    return bridge;
  }

  // 组件化页面若忘记注册 callback bridge，会在这里统一降级到 window bridge，
  // 这样消息链路仍可工作，同时在开发环境暴露缺失注册的问题。
  if (process.env.NODE_ENV === "development") {
    console.warn("[HostBridge] 未显式注册，降级为 windowHostBridge");
  }
  registerHostBridge(windowHostBridge);
  return windowHostBridge;
}

function hasPayloadData(
  data: UnityMessageData,
): data is Record<string, unknown> {
  return (
    !!data &&
    typeof data === "object" &&
    !Array.isArray(data) &&
    Object.keys(data).length > 0
  );
}

function sendOrPost(
  bridge: HostBridge,
  sendFn: () => boolean,
  type: UnityMessageType,
  data: UnityMessageData,
  extraData?: Record<string, unknown>,
): boolean {
  // 保留 bridge 的原生 sendXxx 快捷路径；只有确实携带业务数据时才退化为统一 postMessage。
  if (hasPayloadData(data)) {
    return bridge.postMessage({
      type,
      data: {
        ...extraData,
        ...data,
      },
    });
  }

  return sendFn();
}

export function postMessageToUnity(
  type: UnityMessageType,
  data: UnityMessageData = {},
): boolean {
  if (!isPostMessageSupported()) {
    LogUtil.warn("[UnityMessage] postMessage 不可用，可能不在支持的环境中");
    return false;
  }

  try {
    const message: UnityMessage = { type, data };
    const bridge = getActiveHostBridge();

    if (!bridge.postMessage(message)) {
      return false;
    }

    LogUtil.debug("[UnityMessage] 消息已发送:", message);

    return true;
  } catch (error) {
    LogUtil.error("[UnityMessage] 发送消息失败:", error);
    return false;
  }
}

export function setupUnityMessageListener(): () => void {
  if (typeof window === "undefined") {
    return () => {};
  }

  const handleMessage = async (event: MessageEvent) => {
    try {
      const { data, type, key } = event.data || {};

      // 支持 type 和 key 两种消息格式
      const messageType = type || key;
      if (!messageType) return;

      LogUtil.debug("[UnityMessage] 收到消息:", event.data);

      const handled = await handleUnityReceiveMessage({
        type: messageType,
        data,
        runtime: getUnityMessageRuntime(),
      });

      if (!handled) {
        LogUtil.debug("[UnityMessage] 忽略未处理消息类型:", messageType);
      }
    } catch (error) {
      LogUtil.error("[UnityMessage] 处理接收消息异常:", error);
    }
  };

  window.addEventListener("message", handleMessage);

  return () => {
    window.removeEventListener("message", handleMessage);
  };
}

export function sendBackClickMessage(data: UnityMessageData = {}): boolean {
  executeBetbyKill();
  const bridge = getActiveHostBridge();
  return sendOrPost(
    bridge,
    () => bridge.sendBack(),
    UNITY_MESSAGE_TYPES.BACK_CLICK,
    data,
    {
      action: "back",
    },
  );
}

export function sendRechargeMessage(data: UnityMessageData = {}): boolean {
  const bridge = getActiveHostBridge();
  return sendOrPost(
    bridge,
    () => bridge.sendRecharge(),
    UNITY_MESSAGE_TYPES.RECHARGE,
    data,
  );
}

export function sendLoginMessage(data: UnityMessageData = {}): boolean {
  const bridge = getActiveHostBridge();
  return sendOrPost(
    bridge,
    () => bridge.sendLogin(),
    UNITY_MESSAGE_TYPES.LOGIN,
    data,
  );
}

export function sendTokenExpiredMessage(
  message?: string,
  title?: string,
): boolean {
  LogUtil.info(
    "[UnityMessage] Token 过期触发，显示二次确认弹窗",
    message,
    title,
  );

  const runtime = getGlobalUiRuntime();
  if (!runtime?.showTokenExpiredModal) {
    LogUtil.warn(
      "[UnityMessage] GlobalUiRuntime 未注册，无法显示 TokenExpiredModal",
    );
    return false;
  }

  runtime.showTokenExpiredModal(message, title);
  return true;
}

export function showNetworkRetryModal(
  callbacks?: NetworkRetryModalCallbacks,
): boolean {
  LogUtil.info("[UnityMessage] 显示网络重试弹窗", callbacks);

  const runtime = getGlobalUiRuntime();
  if (!runtime?.showNetworkRetryModal) {
    LogUtil.warn(
      "[UnityMessage] GlobalUiRuntime 未注册，无法显示 NetworkRetryModal",
    );
    return false;
  }

  runtime.showNetworkRetryModal(callbacks);
  return true;
}

export function sendTokenExpiredConfirmMessage(
  data: UnityMessageData = {},
): boolean {
  LogUtil.info("[UnityMessage] 用户确认 Token 过期，发送消息给 Unity");

  // 用户确认退出时需要先销毁 Betby 实例，避免 Unity 和页面同时持有旧会话。
  executeBetbyKill();
  const bridge = getActiveHostBridge();
  return sendOrPost(
    bridge,
    () => bridge.sendTokenExpired(),
    UNITY_MESSAGE_TYPES.TOKEN_EXPIRED,
    data,
    { action: "tokenExpired" },
  );
}

export function sendCurrencyChangeMessage(currency: "sc" | "gc"): boolean {
  return getActiveHostBridge().sendCurrencyChange(currency);
}

export function sendGetWalletMessage(data: UnityMessageData = {}): boolean {
  LogUtil.info("[UnityMessage] 通知业务层刷新钱包 (GetWallet)");
  const bridge = getActiveHostBridge();
  return sendOrPost(
    bridge,
    () => bridge.sendGetWallet(),
    UNITY_MESSAGE_TYPES.GET_WALLET,
    data,
  );
}

export function sendNavigateMessage(url: string): boolean {
  return getActiveHostBridge().sendNavigate(url);
}

export function sendUrlChangeMessage(url: string): boolean {
  return getActiveHostBridge().sendUrlChange(url);
}

export interface SdkReadyMessageData {
  /** 是否初始化成功 */
  success: boolean;
  /** 错误信息，仅在失败时有值 */
  error?: string;
  /** 错误码，仅在失败时有值 */
  code?: number;
  [key: string]: unknown;
}

export function sendSdkReadyMessage(data: SdkReadyMessageData): boolean {
  LogUtil.info("[UnityMessage] SDK 初始化状态通知:", data);
  return postMessageToUnity(UNITY_MESSAGE_TYPES.SDK_READY, data);
}
